/**
 * Device-local persistence for the unsaved design itself.
 *
 * Without it an anonymous (or degraded-mode) draft lived only in memory: a
 * reload reset a 3.0 × 5.4 m design to the 2.5 × 2.0 m defaults, while its
 * build-guide progress — already kept locally — survived. The draft is stored
 * as the same lossless snapshot a share link carries, and restored through the
 * same hydration, so the two can never disagree about what a design is.
 *
 * Keyed like buildProgressLocalStore (sauna type + seed identity): arriving
 * from the wizard or a template is a different seed and starts fresh; a saved
 * design (with an id) is owned by Supabase and never reads this store.
 */

import {
	decodeShareConfig,
	encodeShareConfig,
	type ShareableConfig,
} from "../lib/shareConfig";
import type { SaunaType } from "../types/design";
import { getStorage, hashString } from "./buildProgressLocalStore";

const KEY_PREFIX = "bb:anon-draft:";

export function draftLocalKey(type: SaunaType, seedKey: string): string {
	return `${KEY_PREFIX}${type}:${hashString(seedKey)}`;
}

/** The stored draft, or null when none, storage is blocked, or it is corrupt. */
export function readLocalDraft(key: string): ShareableConfig | null {
	const storage = getStorage();
	if (!storage) return null;
	try {
		const raw = storage.getItem(key);
		return raw ? decodeShareConfig(raw) : null;
	} catch {
		return null;
	}
}

export function writeLocalDraft(key: string, config: ShareableConfig): void {
	const storage = getStorage();
	if (!storage) return;
	try {
		storage.setItem(key, encodeShareConfig(config));
	} catch {
		// Quota exceeded or storage disabled: the draft simply is not kept.
	}
}

export function clearLocalDraft(key: string): void {
	const storage = getStorage();
	if (!storage) return;
	try {
		storage.removeItem(key);
	} catch {
		// Nothing to clear.
	}
}
